import * as React from "react";

import {
    ColumnBuilder,
    StandardToolSpec,
    DataTable,
    DATA_TABLE_MODE_VIEWPORT,
    SelectionHandler,
} from "@impero-com/common/components/dataTable";
import * as DV from "@impero-com/common/utils/dataView";
import { RowClickHandler } from "@impero-com/common/utils/rowClickHandler";

import IconTableView from "img/list-view.svg";
import IconTreeView from "img/tree-view.svg";

import { MultiTreeWithToolBar } from "./multiTree";

class SwitchableTreeView<NODE> extends React.Component<
    SwitchableTreeViewProps<NODE>,
    SwitchableTreeViewState
> {
    constructor(props: SwitchableTreeViewProps<NODE>) {
        super(props);

        this.state = {
            mode: props.defaultMode || Mode.TREE,
        };
    }

    componentWillReceiveProps(
        nextProps: Readonly<SwitchableTreeViewProps<NODE>>
    ) {
        const { lastAddedNodeId } = this.props;
        if (nextProps.lastAddedNodeId !== lastAddedNodeId) {
            this.setState({ lastAddedNodeId: nextProps.lastAddedNodeId });
        }
    }

    render = () => {
        const { mode } = this.state;
        return this.renderMode[mode]();
    };

    renderMode = {
        [Mode.TREE]: () => {
            const {
                dataView,
                columnBuilders,
                tools,
                floatingToolbar,
                toolbarClassName,
                id,
                dataViewModifier,
                openRoots,
                rowClickHandler,
                hideRoots,
                getAddChildWidget,
                visibleColumnIdsForTreeView,
                onVisibleColumnsForTreeViewChanged,
            } = this.props;
            const { lastAddedNodeId } = this.state;

            const treeColumnBuilders =
                columnBuilders &&
                Object.fromEntries(
                    Object.entries(columnBuilders).map(([key, cb]) => {
                        if (typeof cb === "function") {
                            return [key, cb];
                        } else {
                            return [key, cb[Mode.TREE]];
                        }
                    })
                );

            const treeTools = (tools ?? this.defaultTools())
                .map((def) => {
                    switch (def.type) {
                        case "TreeMode":
                            return def.tool;
                        case "TableMode":
                            return undefined;
                        default:
                            return def;
                    }
                })
                .filter((def) => def !== undefined) as
                | Array<StandardToolSpec<DV.NodeDescriptor<NODE>>>
                | undefined;

            const treeDataView = (
                (dataViewModifier ?? {})[Mode.TREE] ?? identity
            )(dataView);

            return (
                <MultiTreeWithToolBar<NODE>
                    columnBuilders={treeColumnBuilders}
                    tools={treeTools}
                    dataView={treeDataView}
                    floatingToolbar={floatingToolbar}
                    toolbarClassName={toolbarClassName}
                    id={id}
                    openRoots={openRoots}
                    rowClickHandler={rowClickHandler}
                    hideRoots={hideRoots}
                    getAddChildWidget={getAddChildWidget}
                    lastAddedNodeId={lastAddedNodeId}
                    visibleColumnIds={visibleColumnIdsForTreeView}
                    onVisibleColumnsChanged={onVisibleColumnsForTreeViewChanged}
                />
            );
        },
        [Mode.TABLE]: () => {
            const {
                dataView,
                columnBuilders,
                tools,
                id,
                dataViewModifier,
                rowClickHandler,
                calcMinTableHeight,
                selectionHandler,
                visibleColumnIdsForTableView,
                onVisibleColumnsForTableViewChanged,
            } = this.props;

            const tableColumnBuilders =
                columnBuilders &&
                Object.fromEntries(
                    Object.entries(columnBuilders).map(([key, cb]) => {
                        if (typeof cb === "function") {
                            return [key, cb];
                        } else {
                            return [key, cb[Mode.TABLE]];
                        }
                    })
                );

            const tableTools = (tools ?? this.defaultTools())
                .map((def) => {
                    switch (def.type) {
                        case "TableMode":
                            return def.tool;
                        case "TreeMode":
                            return undefined;
                        default:
                            return def;
                    }
                })
                .filter((def) => def !== undefined) as Array<
                StandardToolSpec<DV.NodeDescriptor<NODE>>
            >;

            const tableDataView = (
                (dataViewModifier ?? {})[Mode.TABLE] ?? identity
            )(dataView);
            return (
                <DataTable
                    columnBuilders={tableColumnBuilders}
                    selectionHandler={selectionHandler}
                    tools={tableTools}
                    dataView={tableDataView}
                    calcMinTableHeight={calcMinTableHeight}
                    id={id}
                    rowClickHandler={rowClickHandler}
                    mode={DATA_TABLE_MODE_VIEWPORT}
                    visibleColumnIds={visibleColumnIdsForTableView}
                    onVisibleColumnsChanged={
                        onVisibleColumnsForTableViewChanged
                    }
                />
            );
        },
    };

    defaultTools = (): Array<
        StandardToolSpec<DV.NodeDescriptor<NODE>> | SwitchableToolSpec<NODE>
    > => {
        const { mode } = this.state;
        const { onModeChange } = this.props;
        return [
            { type: "Spacer", flex: "auto" },
            {
                type: "Toggle",
                toggleButtons: [
                    { id: Mode.TREE, type: "ImageToggle", image: IconTreeView },
                    {
                        id: Mode.TABLE,
                        type: "ImageToggle",
                        image: IconTableView,
                    },
                ],
                activeButtonId: mode,
                onCommand: (mode: ModeType) => {
                    this.setState({ mode, lastAddedNodeId: undefined });
                    if (onModeChange) {
                        onModeChange(mode);
                    }
                },
            },
            { type: "TreeMode", tool: { type: "ViewAdjuster" } },
            { type: "TableMode", tool: { type: "ViewAdjuster" } },
        ];
    };
}

interface SwitchableTreeViewProps<NODE> {
    id: string;
    dataView: DV.DataView<DV.NodeDescriptor<NODE>>;
    rowClickHandler?: RowClickHandler<DV.NodeDescriptor<NODE>>;
    dataViewModifier?: Partial<
        Record<ModeType, DataViewModifier<DV.NodeDescriptor<NODE>>>
    >;
    defaultMode?: ModeType;
    // Be careful with column builders, the item may be expanded.
    // That is why ColumnBuilder is typed with any instead of NODE/Expando<NODE>.
    columnBuilders?: {
        [extractorId: string]:
            | ColumnBuilder<any, any>
            | Record<ModeType, ColumnBuilder<any, any>>;
    };
    openRoots?: boolean;
    lastAddedNodeId?: string;
    hideRoots?: boolean;

    toolbarClassName?: string;
    floatingToolbar?: boolean;
    maxHeight?: number;
    calcMinTableHeight?: () => number;
    tools?: Array<
        StandardToolSpec<DV.NodeDescriptor<NODE>> | SwitchableToolSpec<NODE>
    >;

    getAddChildWidget?: (
        parentNode: NODE,
        depth: number
    ) => React.ReactNode | null;
    selectionHandler?: SelectionHandler<DV.NodeDescriptor<NODE>>;

    visibleColumnIdsForTreeView?: Set<string>;
    onVisibleColumnsForTreeViewChanged?: (
        visibleColumnIds: Set<string>
    ) => void;

    visibleColumnIdsForTableView?: Set<string>;
    onVisibleColumnsForTableViewChanged?: (
        visibleColumnIds: Set<string>
    ) => void;

    onModeChange?: (mode: ModeType) => void;
}

interface SwitchableTreeViewState {
    mode: ModeType;
    lastAddedNodeId?: string;
}

const Mode = {
    TREE: "TREE" as const,
    TABLE: "TABLE" as const,
};
type ModeType = typeof Mode.TREE | typeof Mode.TABLE;

type DataViewModifier<T> = (dataView: DV.DataView<T>) => DV.DataView<T>;

function identity<T>(t: T): T {
    return t;
}

type SwitchableToolSpec<NODE> =
    | { type: "TreeMode"; tool: StandardToolSpec<DV.NodeDescriptor<NODE>> }
    | { type: "TableMode"; tool: StandardToolSpec<NODE> };

export { SwitchableTreeView, Mode, type ModeType, type SwitchableToolSpec };
